// =============================================================================
// Transaction Management (legacy frmTransactionDashboard).
// Two modes, faithful to the legacy form:
//   * Simple  — fast cashier list of transactions (header columns only).
//   * Detailed — full datagrid with each transaction's line items rolled up,
//                expandable inline (lazy-loaded so the grid stays fast).
// Performance: the invoices table has ~100k rows, so the list selects only the
// header columns (+ embedded customer) and uses a planner-estimated row count
// instead of an exact count(*) on every load (see listInvoices / migration 014).
// =============================================================================
import { useState, useEffect, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, RotateCcw, Banknote, ChevronRight, ChevronDown, ExternalLink, Printer, Ban } from 'lucide-react'
import { listInvoices, listInvoiceItems, getClerkNameMaps, voidInvoice, getInvoiceWithItems } from '../services/data'
import { PageHeader, Card, SearchInput, Pagination, Spinner, FilterBar, Button } from '../components/shared/ui'
import { currency, dateTime, customerLabel, number } from '../lib/format'
import { printHtml } from '../lib/print'
import { THOMSONS_LOGO } from '../lib/assets'
import { useRole } from '../context/RoleContext'
import ManagerOverride from '../components/shared/ManagerOverride'
import { useServerList } from '../lib/list'
import type { Sort, Filter } from '../lib/list'
import type { Invoice, InvoiceItem } from '../types'
import { toast } from 'react-toastify'

const PAYMENT_OPTIONS = ['Cash', 'Credit Card', 'Debit Card', 'Check', 'Gift Card', 'Store Credit', 'In-House Charge', 'Donation']
const STATUS_OPTIONS = ['Completed', 'Suspended', 'In Progress', 'Interrupted', 'Void']
// Unfinished transactions reopen in the Sales Register to be continued (legacy
// frmTransactionDashboard -> frmExpressInvoicing); completed ones open read-only.
const RESUMABLE_STATUSES = ['Suspended', 'In Progress', 'Interrupted']

const PAGE_SIZE = 25

const toISODate = (d: Date) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x }
const endOfDay = (d: Date) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x }
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x }
const addMonths = (d: Date, n: number) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x }
const startOfWeek = (d: Date) => { const x = startOfDay(d); x.setDate(x.getDate() - x.getDay()); return x }
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1)
const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999)
const ytdStart = (d: Date) => new Date(d.getFullYear(), 0, 1)

const DATE_PRESETS = (anchor = new Date()) => [
  { label: 'Today', from: toISODate(startOfDay(anchor)), to: toISODate(endOfDay(anchor)) },
  { label: 'Yesterday', from: toISODate(startOfDay(addDays(anchor, -1))), to: toISODate(endOfDay(addDays(anchor, -1))) },
  { label: 'This Week', from: toISODate(startOfWeek(anchor)), to: toISODate(endOfDay(anchor)) },
  { label: 'Last Week', from: toISODate(startOfWeek(addDays(anchor, -7))), to: toISODate(endOfDay(addDays(startOfWeek(anchor), -1))) },
  { label: 'This Month', from: toISODate(startOfMonth(anchor)), to: toISODate(endOfDay(anchor)) },
  { label: 'Last Month', from: toISODate(startOfMonth(addMonths(anchor, -1))), to: toISODate(endOfMonth(addMonths(anchor, -1))) },
  { label: 'Last 3 Months', from: toISODate(startOfMonth(addMonths(anchor, -3))), to: toISODate(endOfDay(anchor)) },
  { label: 'YTD', from: toISODate(ytdStart(anchor)), to: toISODate(endOfDay(anchor)) },
]

type InvoiceRow = Invoice & {
  customers?: { name: string | null; company_name: string | null; first_name: string | null; last_name: string | null } | null
}

function StatusBadge({ inv }: { inv: Invoice }) {
  if (inv.is_void) return <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 text-xs">Void</span>
  const s = inv.status || 'Completed'
  const cls = s === 'Completed' ? 'bg-green-100 text-green-700'
    : s === 'Suspended' ? 'bg-amber-100 text-amber-700'
    : s === 'In Progress' ? 'bg-blue-100 text-blue-700'
    : s === 'Interrupted' ? 'bg-orange-100 text-orange-700'
    : 'bg-gray-100 text-gray-700'
  return <span className={`px-2 py-0.5 rounded text-xs ${cls}`}>{s}</span>
}

function Th({ label, col, sort, onSort, className = '' }: {
  label: string; col?: string; sort: Sort | null; onSort: (c: string) => void; className?: string
}) {
  const active = col && sort?.column === col
  return (
    <th
      onClick={() => col && onSort(col)}
      className={`text-left font-semibold text-gray-600 px-3 py-2.5 ${col ? 'cursor-pointer select-none hover:text-gray-900' : ''} ${className}`}
    >
      <span className="inline-flex items-center gap-1">{label}{active && <span className="text-green-600">{sort!.dir === 'asc' ? '▲' : '▼'}</span>}</span>
    </th>
  )
}

export default function Invoices() {
  const navigate = useNavigate()
  const [detailed, setDetailed] = useState(false)
  const [expanded, setExpanded] = useState<Record<string, InvoiceItem[] | 'loading'>>({})
  const [clerkMaps, setClerkMaps] = useState<{ byUuid: Record<string, string>; byLegacy: Record<number, string> }>({ byUuid: {}, byLegacy: {} })
  const { can } = useRole()
  const [voidTarget, setVoidTarget] = useState<Invoice | null>(null)
  const [otherFilters, setOtherFilters] = useState<Filter[]>([])
  const [dateFrom, setDateFrom] = useState(() => toISODate(startOfDay(new Date())))
  const [dateTo, setDateTo] = useState(() => toISODate(endOfDay(new Date())))
  const { rows, count, page, search, sort, loading, setPage, setSearch, setFilters, toggleSort, reload } =
    useServerList<Invoice>(listInvoices, { pageSize: PAGE_SIZE, defaultSort: { column: 'invoice_date', dir: 'desc' } })

  const applyFilters = (from: string, to: string, others: Filter[]) => {
    const next: Filter[] = [...others]
    if (from) next.push({ column: 'invoice_date', op: 'gte', value: from })
    if (to) next.push({ column: 'invoice_date', op: 'lte', value: `${to}T23:59:59.999` })
    setFilters(next)
  }

  useEffect(() => { applyFilters(dateFrom, dateTo, otherFilters) }, [])

  const pickPreset = (from: string, to: string) => {
    setDateFrom(from)
    setDateTo(to)
    applyFilters(from, to, otherFilters)
  }

  const onDateChange = (from: string, to: string) => {
    setDateFrom(from)
    setDateTo(to)
    applyFilters(from, to, otherFilters)
  }

  const onFilterBarChange = (filters: Filter[]) => {
    setOtherFilters(filters)
    applyFilters(dateFrom, dateTo, filters)
  }

  // Open a row: unfinished transactions are reopened in the Sales Register to be
  // continued; finished ones open the read-only invoice detail.
  const isResumable = (inv: Invoice) => !inv.is_void && RESUMABLE_STATUSES.includes(inv.status ?? '')
  const openInvoice = (inv: Invoice) =>
    isResumable(inv) ? navigate(`/sales?resume=${inv.id}`) : navigate(`/invoices/${inv.id}`)

  // Resolve clerk names once. Web sales stamp clerk_id (auth UUID); historic
  // migrated invoices only have legacy_clerk_id (legacy fnClerkID).
  useEffect(() => { getClerkNameMaps().then(setClerkMaps).catch(() => {}) }, [])
  const clerkName = (inv: Invoice) => {
    const uuid = inv.clerk_id as string | null
    if (uuid && clerkMaps.byUuid[uuid]) return clerkMaps.byUuid[uuid]
    const legacy = inv.legacy_clerk_id as number | null
    if (legacy != null) return clerkMaps.byLegacy[legacy] ?? `Clerk #${legacy}`
    return '—'
  }

  const toggleRow = async (inv: Invoice) => {
    if (expanded[inv.id]) {
      setExpanded(prev => { const c = { ...prev }; delete c[inv.id]; return c })
      return
    }
    setExpanded(prev => ({ ...prev, [inv.id]: 'loading' }))
    try {
      const items = await listInvoiceItems(inv.id)
      setExpanded(prev => ({ ...prev, [inv.id]: items }))
    } catch {
      setExpanded(prev => ({ ...prev, [inv.id]: [] }))
    }
  }

  const performVoid = async (inv: Invoice) => {
    try { await voidInvoice(inv.id); toast.success(`Invoice #${inv.legacy_invoice_number ?? ''} voided`); reload() }
    catch (e) { console.error(e); toast.error('Failed to void invoice') }
  }

  const doVoid = (inv: Invoice) => {
    if (inv.is_void) return
    if (!window.confirm(`Void invoice #${inv.legacy_invoice_number ?? ''}? This cannot be undone.`)) return
    if (!can('perm_void_past_transactions') && !can('perm_void_transaction')) { setVoidTarget(inv); return }
    performVoid(inv)
  }

  // Print a receipt directly from the list (legacy btnPrintReceipt) by fetching
  // the invoice's items + payments on demand.
  const printReceipt = async (inv: Invoice) => {
    try {
      const data = await getInvoiceWithItems(inv.id)
      if (!data) { toast.error('Invoice not found'); return }
      const { invoice, items, payments } = data
      const rowsHtml = items.map(i => `<tr><td>${i.product_name ?? ''}</td><td class="right">${i.qty ?? ''}</td><td class="right">${currency(i.price)}</td><td class="right">${currency(i.total_price)}</td></tr>`).join('')
      const pays = payments.map(p => `<tr><td class="label">${p.payment_method ?? ''}</td><td class="right">${currency(p.amount)}</td></tr>`).join('')
      printHtml(`Receipt #${invoice.legacy_invoice_number ?? ''}`, `
        <div style="text-align:center"><img src="${THOMSONS_LOGO}" style="width:120px;height:auto" alt=""/><h2>Thomson's Landscaping &amp; Garden Center</h2><p>Invoice #${invoice.legacy_invoice_number ?? ''}<br/>${dateTime(invoice.invoice_date)}</p></div><hr/>
        <table><thead><tr><th>Item</th><th class="right">Qty</th><th class="right">Price</th><th class="right">Total</th></tr></thead><tbody>${rowsHtml}</tbody></table>
        <table class="totals"><tbody>
          <tr><td class="label">Subtotal</td><td class="right">${currency(invoice.taxable_amount ?? invoice.total)}</td></tr>
          <tr><td class="label">Tax</td><td class="right">${currency(invoice.tax)}</td></tr>
          <tr><td class="label" style="font-weight:bold">Total</td><td class="right" style="font-weight:bold">${currency(invoice.total_price ?? invoice.total)}</td></tr>
          ${pays}</tbody></table>
        <p style="text-align:center;margin-top:24px">Thank you!</p>`)
    } catch (e) { console.error(e); toast.error('Failed to load receipt') }
  }

  // Page totals for the grid footer (legacy ColumnFooters).
  const pageTotals = (rows as InvoiceRow[]).reduce(
    (a, r) => ({ qty: a.qty + (r.total_qty ?? 0), total: a.total + (r.total ?? 0) }),
    { qty: 0, total: 0 })

  const colCount = 11

  return (
    <div>
      <PageHeader title="Transaction Management" subtitle={`~${count.toLocaleString()} transactions`}
        actions={
          <>
            <Button variant="secondary" onClick={() => navigate('/sales')}><Plus size={16} /> New Sale</Button>
            <Button variant="secondary" onClick={() => navigate('/refunds')}><RotateCcw size={16} /> Refund / Void</Button>
            <Button variant="secondary" onClick={() => navigate('/payouts')}><Banknote size={16} /> Payouts</Button>
          </>
        } />
      <Card className="p-4">
        <div className="mb-4 flex flex-col gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]"><SearchInput value={search} onChange={setSearch} placeholder="Search by invoice number…" /></div>
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
              <button
                onClick={() => setDetailed(false)}
                className={`px-3 py-2 font-medium ${!detailed ? 'bg-green-700 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
              >Simple</button>
              <button
                onClick={() => setDetailed(true)}
                className={`px-3 py-2 font-medium ${detailed ? 'bg-green-700 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
              >Detailed</button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {DATE_PRESETS().map(p => {
              const active = dateFrom === p.from && dateTo === p.to
              return (
                <button
                  key={p.label}
                  onClick={() => pickPreset(p.from, p.to)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    active ? 'bg-green-700 text-white border-green-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {p.label}
                </button>
              )
            })}
            <div className="flex items-center gap-1 ml-2">
              <input
                type="date"
                className="px-2 py-1.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                value={dateFrom}
                onChange={e => onDateChange(e.target.value, dateTo)}
              />
              <span className="text-gray-400 text-xs">to</span>
              <input
                type="date"
                className="px-2 py-1.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                value={dateTo}
                onChange={e => onDateChange(dateFrom, e.target.value)}
              />
            </div>
          </div>
          <FilterBar
            onChange={onFilterBarChange}
            fields={[
              { key: 'payment', label: 'Payment', column: 'payment_method', type: 'select', options: PAYMENT_OPTIONS.map(p => ({ label: p, value: p })) },
              { key: 'status', label: 'Status', column: 'status', type: 'select', options: STATUS_OPTIONS.map(s => ({ label: s, value: s })) },
              { key: 'void', label: 'Voided', column: 'is_void', type: 'boolean' },
            ]}
          />
        </div>
        {loading ? (
          <Spinner label="Loading transactions…" />
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-gray-500">No transactions found.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    {detailed && <th className="w-8 px-2 py-2.5" />}
                    <Th label="Invoice #" col="legacy_invoice_number" sort={sort} onSort={toggleSort} />
                    <Th label="Date" col="invoice_date" sort={sort} onSort={toggleSort} />
                    <Th label="Clerk" col="legacy_clerk_id" sort={sort} onSort={toggleSort} />
                    {detailed && <Th label="Customer" sort={sort} onSort={toggleSort} />}
                    {detailed && <Th label="Qty" sort={sort} onSort={toggleSort} className="text-right" />}
                    <Th label="Payment" col="payment_method" sort={sort} onSort={toggleSort} />
                    <Th label="Total" col="total" sort={sort} onSort={toggleSort} className="text-right" />
                    <Th label="Status" col="status" sort={sort} onSort={toggleSort} />
                    <th className="w-10 px-2 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {(rows as InvoiceRow[]).map(inv => {
                    const exp = expanded[inv.id]
                    return (
                      <Fragment key={inv.id}>
                        <tr
                          onClick={() => detailed ? toggleRow(inv) : openInvoice(inv)}
                          className={`border-b border-gray-100 cursor-pointer hover:bg-green-50 ${inv.is_void ? 'bg-red-50/40' : ''}`}
                        >
                          {detailed && (
                            <td className="px-2 py-2.5 text-gray-400">
                              {exp ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </td>
                          )}
                          <td className="px-3 py-2.5 font-medium text-gray-900">#{inv.legacy_invoice_number ?? '—'}</td>
                          <td className="px-3 py-2.5 text-gray-700">{dateTime(inv.invoice_date)}</td>
                          <td className="px-3 py-2.5 text-gray-700">{clerkName(inv)}</td>
                          {detailed && <td className="px-3 py-2.5 text-gray-700">{inv.customer_id ? customerLabel(inv.customers) : 'Cash Sale'}</td>}
                          {detailed && <td className="px-3 py-2.5 text-right text-gray-700">{number(inv.total_qty)}</td>}
                          <td className="px-3 py-2.5 text-gray-700">{inv.payment_method || '—'}</td>
                          <td className="px-3 py-2.5 text-right font-medium text-gray-900">{currency(inv.total)}</td>
                          <td className="px-3 py-2.5"><StatusBadge inv={inv} /></td>
                          <td className="px-2 py-2.5">
                            <div className="flex items-center gap-2 justify-end">
                              <button onClick={e => { e.stopPropagation(); printReceipt(inv) }} className="text-gray-400 hover:text-green-700" title="Print receipt"><Printer size={15} /></button>
                              {!inv.is_void && <button onClick={e => { e.stopPropagation(); doVoid(inv) }} className="text-gray-400 hover:text-red-600" title="Void"><Ban size={15} /></button>}
                              <button onClick={e => { e.stopPropagation(); openInvoice(inv) }} className="text-gray-400 hover:text-green-700" title={isResumable(inv) ? 'Continue transaction' : 'Open invoice'}><ExternalLink size={15} /></button>
                            </div>
                          </td>
                        </tr>
                        {detailed && exp && (
                          <tr className="bg-gray-50/70">
                            <td colSpan={colCount} className="px-4 py-3">
                              {exp === 'loading' ? (
                                <div className="text-gray-500 text-xs py-2">Loading items…</div>
                              ) : exp.length === 0 ? (
                                <div className="text-gray-500 text-xs py-2">No line items.</div>
                              ) : (
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-gray-500">
                                      <th className="text-left font-medium px-2 py-1">Code</th>
                                      <th className="text-left font-medium px-2 py-1">Item</th>
                                      <th className="text-right font-medium px-2 py-1">Qty</th>
                                      <th className="text-right font-medium px-2 py-1">Price</th>
                                      <th className="text-right font-medium px-2 py-1">Total</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {exp.map(it => (
                                      <tr key={it.id} className="border-t border-gray-200">
                                        <td className="px-2 py-1 text-gray-500">{it.code || it.scan_number || it.sku || it.legacy_product_code?.toString() || '—'}</td>
                                        <td className="px-2 py-1 text-gray-800">{it.product_name ?? '—'}</td>
                                        <td className="px-2 py-1 text-right text-gray-700">{number(it.qty)}</td>
                                        <td className="px-2 py-1 text-right text-gray-700">{currency(it.price)}</td>
                                        <td className="px-2 py-1 text-right text-gray-800">{currency(it.total_price)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold text-gray-800">
                    {detailed && <td className="px-2 py-2.5" />}
                    <td className="px-3 py-2.5" colSpan={detailed ? 3 : 3}>Page totals ({rows.length})</td>
                    {detailed && <td className="px-3 py-2.5 text-right">{number(pageTotals.qty)}</td>}
                    <td className="px-3 py-2.5" />
                    <td className="px-3 py-2.5 text-right">{currency(pageTotals.total)}</td>
                    <td className="px-3 py-2.5" />
                    <td className="px-2 py-2.5" />
                  </tr>
                </tfoot>
              </table>
            </div>
            <Pagination page={page} pageSize={PAGE_SIZE} total={count} onPage={setPage} />
          </>
        )}
      </Card>

      {voidTarget && (
        <ManagerOverride
          perm="perm_void_past_transactions"
          title="Manager Authorization — Void"
          message={`You do not have permission to void invoice #${voidTarget.legacy_invoice_number ?? ''}. A manager must authorize.`}
          onApproved={async () => { const t = voidTarget; setVoidTarget(null); if (t) await performVoid(t) }}
          onClose={() => setVoidTarget(null)}
        />
      )}
    </div>
  )
}
